/* eslint no-restricted-imports: ["error", { patterns: [{
    group: ["*"],
    message: "Please consider the importance of this import, and add exceptions if really necessary"
}]}]
--------
This module is used for landing pages, which need to be very light and minimal on imports. If you
need to change some functionality here, consider looking at lib/utils.ts
*/

export type ClipboardContent = {
  plain?: string;
  html?: string;
};

const setClipboardContents = (event: ClipboardEvent, content: ClipboardContent) => {
  if (event.clipboardData) {
    for (const [format, data] of Object.entries(content)) {
      if (data) {
        event.clipboardData.setData(`text/${format}`, data);
      }
    }
  }
  event.preventDefault();
};

/**
 * Copies plain text and optionally HTML content to the clipboard.
 * Uses the modern Clipboard API for better compatibility.
 */
export async function copyToClipboard(content: ClipboardContent): Promise<boolean> {
  // Use modern Clipboard API when available
  if (!navigator.clipboard) {
    return copyToClipboardOldWay(content);
  }
  try {
    // HTML goes in alongside plain text so rich editors (email) keep links and plain ones (chat)
    // still get text. Browsers without ClipboardItem fall back to plain text alone.
    if (content.html && typeof ClipboardItem !== "undefined") {
      const items: Record<string, Blob> = {
        "text/html": new Blob([content.html], { type: "text/html" }),
      };
      if (content.plain) {
        items["text/plain"] = new Blob([content.plain], { type: "text/plain" });
      }
      await navigator.clipboard.write([new ClipboardItem(items)]);
      return true;
    }

    if (content.plain) {
      await navigator.clipboard.writeText(content.plain);
      return true;
    }
  } catch (e) {
    console.error("Failed to copy to clipboard:", JSON.stringify(e));
    return false;
  }

  return false;
}

/**
 * This function uses the now depracted `execCommand` to copy content to the
 * clipboard. Just a fallback for older browsers that don't have the navigator
 * API built in
 * @param content
 * @returns
 */
function copyToClipboardOldWay(content: ClipboardContent): boolean {
  const copyListener = (e: ClipboardEvent) => setClipboardContents(e, content);
  let copied = false;

  // this may be overkill as `copy` should not be problematic, but it is nice if the caller
  // of this function can have feedback that copying was successful
  try {
    document.addEventListener<"copy">("copy", copyListener);
    copied = document.execCommand("copy");
  } catch (e) {
    copied = false;
    console.error("Failed to copy:", JSON.stringify(e));
  } finally {
    document.removeEventListener("copy", copyListener);
  }

  return copied;
}
